/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Breadcrumbs (#683, W2 Phase 1) — the trail under the top bar.
 *
 * Per decision **D1** this ships **route-derived**: `group › surface`, giving IA
 * context for wherever the operator currently is. The design's richer behaviour
 * is a *journey* trail — investigating from Sessions into Metrics Console into
 * Pipeline Diagnostics pushes crumbs, and each earlier crumb jumps back — which
 * needs the shared investigation context from #817.
 *
 * So the trail is a **prop, not a computation**: `AppLayout` passes the
 * route-derived crumbs today, and #817 can pass journey crumbs instead without
 * touching this component.
 */

import React from 'react';
import { chakra, Flex, Text } from '@chakra-ui/react';
import { useGuardedNavigate } from '../ui/UnsavedWorkGuard';
import { NavIcon } from './nav-icons';

export interface Crumb {
  label: string;
  /** Omit for the current surface — the last crumb is never a link. */
  path?: string;
}

export interface BreadcrumbsProps {
  crumbs: Crumb[];
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ crumbs }) => {
  /* Guarded, because a breadcrumb is a nav away and a nav away is an exit (`#1245`). */
  const go = useGuardedNavigate();
  if (crumbs.length === 0) return null;

  return (
    <Flex
      as="nav"
      aria-label="Breadcrumb"
      align="center"
      gap={2}
      px={6}
      py={2.5}
      bg="bg.card"
      borderBottomWidth="1px"
      borderBottomColor="border.default"
      fontSize="12.5px"
      flexShrink={0}
      overflowX="auto"
    >
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <React.Fragment key={`${crumb.label}-${i}`}>
            {i > 0 && (
              <NavIcon
                name="chevron-right"
                size={13}
                style={{ flexShrink: 0, color: 'var(--chakra-colors-fg-muted)' }}
              />
            )}
            {isLast || !crumb.path ? (
              <Text whiteSpace="nowrap" color="fg.default" fontWeight="500">
                {crumb.label}
              </Text>
            ) : (
              <chakra.button
                type="button"
                whiteSpace="nowrap"
                color="fg.muted"
                cursor="pointer"
                _hover={{ color: 'accent.solid' }}
                onClick={() => go(crumb.path as string)}
              >
                {crumb.label}
              </chakra.button>
            )}
          </React.Fragment>
        );
      })}
    </Flex>
  );
};
